/**
 * Locale-aware formatting (spec 10.2: "dates, numbers and currency through
 * `Intl`, in the active locale"). Every helper takes the BCP-47 tag from
 * i18next and degrades to a stable string instead of throwing on bad input —
 * registry data contains plenty of unparseable dates.
 */

const FALLBACK = '—';

function isLocaleUsable(tag: string): boolean {
  try {
    // Throws RangeError on a structurally invalid tag.
    Intl.DateTimeFormat.supportedLocalesOf([tag]);
    return true;
  } catch {
    return false;
  }
}

function safeLocale(tag: string): string {
  return isLocaleUsable(tag) ? tag : 'es';
}

/** `12.03.2024` style short date, or the raw string when unparseable. */
export function formatDate(
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  if (!value) return FALLBACK;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(safeLocale(locale), options).format(date);
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  return formatDate(value, locale, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatNumber(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return FALLBACK;
  return new Intl.NumberFormat(safeLocale(locale)).format(value);
}

/**
 * Money. `currency` comes from the registry (BRL/ARS/USD/…); an unknown code
 * would make `Intl` throw, so it is validated first and the plain number is
 * shown when it is not a real ISO code.
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return FALLBACK;
  const code = (currency ?? '').toUpperCase();
  const localeTag = safeLocale(locale);
  if (!/^[A-Z]{3}$/.test(code)) return new Intl.NumberFormat(localeTag).format(value);
  try {
    return new Intl.NumberFormat(localeTag, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return new Intl.NumberFormat(localeTag).format(value);
  }
}

/** USD prices on the pricing page are stored in USD (config/app.config.yaml). */
export function formatUsd(value: number, locale: string, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat(safeLocale(locale), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits,
  }).format(value);
}

export function formatPercent(value: number | null | undefined, locale: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return FALLBACK;
  return new Intl.NumberFormat(safeLocale(locale), {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export function formatLatency(ms: number | null | undefined, locale: string): string {
  if (ms === null || ms === undefined) return FALLBACK;
  if (ms < 1000) return `${formatNumber(Math.round(ms), locale)} ms`;
  return `${new Intl.NumberFormat(safeLocale(locale), { maximumFractionDigits: 1 }).format(ms / 1000)} s`;
}
